import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Star,
  ArrowLeft,
  Phone,
  MessageSquare,
  ExternalLink,
  Heart,
  Globe
} from "lucide-react";
import PeerSupportDisclaimer from "../components/PeerSupportDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resources = ({ user }) => {
  const navigate = useNavigate();
  const [resources, setResources] = useState({ hotlines: [], resources: [] });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error("Error loading resources:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHotlineIcon = (type) => {
    switch (type) {
      case "primary":
        return Phone;
      case "text":
        return MessageSquare;
      case "emergency":
        return Heart;
      default:
        return Phone;
    }
  };

  const getHotlineBadge = (type) => {
    switch (type) {
      case "primary":
        return { label: "Primary", variant: "default" };
      case "text":
        return { label: "Text", variant: "secondary" };
      case "emergency":
        return { label: "Emergency", variant: "destructive" };
      default:
        return { label: "Support", variant: "outline" };
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-40">
        <div className="px-4 py-3 flex items-center">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-primary" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Crisis Resources
              </span>
              <span className="text-xs text-primary font-bold">™</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-16 pb-8 px-4 max-w-3xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-8 py-6">
            {/* Emergency Banner */}
            <Card className="bg-accent/10 border-accent/30 card-tactical">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                      In immediate danger?
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Call <a href="tel:988" className="font-bold text-accent underline">988</a> (Suicide & Crisis Lifeline) or{" "}
                      <a href="tel:911" className="font-bold text-accent underline">911</a> for emergencies
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Crisis Hotlines */}
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                <Phone className="w-5 h-5 inline mr-2 text-secondary" />
                Crisis Hotlines
              </h2>
              
              <div className="space-y-3">
                {resources.hotlines.map((hotline, index) => {
                  const Icon = getHotlineIcon(hotline.type);
                  const badgeInfo = getHotlineBadge(hotline.type);
                  
                  return (
                    <Card key={index} className="card-tactical" data-testid={`hotline-${index}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            hotline.type === "primary" ? "bg-primary" :
                            hotline.type === "emergency" ? "bg-destructive" :
                            "bg-secondary"
                          }`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                                {hotline.name}
                              </h3>
                              <Badge variant={badgeInfo.variant} className="text-xs">
                                {badgeInfo.label}
                              </Badge>
                            </div>
                            {hotline.type === "text" ? (
                              <a 
                                href="sms:741741?body=HOME"
                                className="text-lg font-bold text-accent hover:underline"
                              >
                                {hotline.phone}
                              </a>
                            ) : (
                              <a 
                                href={`tel:${hotline.phone.replace(/[^0-9]/g, '')}`}
                                className="text-lg font-bold text-accent hover:underline"
                              >
                                {hotline.phone}
                              </a>
                            )}
                            <p className="text-sm text-muted-foreground mt-1">
                              {hotline.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* Online Resources */}
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                <Globe className="w-5 h-5 inline mr-2 text-secondary" />
                Online Resources
              </h2>
              
              <div className="grid gap-3">
                {resources.resources.map((resource, index) => (
                  <Card key={index} className="card-tactical" data-testid={`resource-${index}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                            {resource.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {resource.description}
                          </p>
                        </div>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(resource.url, "_blank")}
                          className="flex-shrink-0"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Visit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Additional Info */}
            <Card className="card-tactical bg-muted/50">
              <CardContent className="p-6 text-center">
                <Heart className="w-10 h-10 text-secondary mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                  You're Not Alone
                </h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  If you're struggling, reach out. These resources are staffed by people who understand 
                  and want to help. Your call could be the first step toward feeling better.
                </p>
              </CardContent>
            </Card>

            {/* Peer Support Disclaimer */}
            <PeerSupportDisclaimer className="rounded-lg" />
          </div>
        )}
      </main>
    </div>
  );
};

export default Resources;
